/**
 * TileDefaultsPanel - Separate draggable panel for tile appearance settings
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, GripHorizontal } from 'lucide-react';
import ColorPicker from './Grid/ColorPicker';
import { useAppearanceSettings } from '../contexts/AppearanceSettingsContext';
import { showAlert } from './common/AppDialogs';

interface TileDefaultsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  modalRef: React.RefObject<HTMLDivElement>;
}

const tileTypes = [
  { id: 'content', name: 'Content Tile' },
  { id: 'form', name: 'Form Tile' },
  { id: 'external_link', name: 'External Link Tile' },
  { id: 'shortcut', name: 'Shortcut Tile' },
  { id: 'nested_page', name: 'Nested Page Tile' },
  { id: 'roadmap', name: 'Roadmap Tile' }
];

export default function TileDefaultsPanel({
  isOpen,
  onClose,
  modalRef
}: TileDefaultsPanelProps) {
  const contextValue = useAppearanceSettings();
  const {
    selectedTile,
    selectedHeader,
    selectedRoadmapState,
    tileConfigs,
    headerConfigs,
    updateTileConfig,
    updateHeaderConfig,
    setSelectedRoadmapState
  } = contextValue;

  // Debug: Log the entire context and selectedRoadmapState
  console.log('🎛️ TileDefaultsPanel full context:', contextValue);
  console.log('🎛️ TileDefaultsPanel selectedRoadmapState:', selectedRoadmapState);
  console.log('🎛️ TileDefaultsPanel setSelectedRoadmapState:', setSelectedRoadmapState);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Get config for the current context (roadmap state-specific or regular)
  const getConfig = () => {
    if (selectedTile === 'roadmap') {
      // For roadmap tiles, return merged config for display, but track what's state-specific
      const roadmapConfig = tileConfigs.roadmap || {};
      // Convert state names to match context keys
      const stateKeyMap = {
        'todo': 'todoState',
        'in_progress': 'inProgressState', 
        'complete': 'completeState'
      };
      const stateKey = stateKeyMap[selectedRoadmapState];
      const stateConfig = roadmapConfig[stateKey] || {};
      
      // Return merged config so settings show current effective values
      // But mark which properties are state-specific for proper updating
      const mergedConfig = { ...roadmapConfig, ...stateConfig };
      mergedConfig._stateSpecific = stateConfig; // Track state-specific values
      
      console.log('📋 getConfig for roadmap:', {
        selectedRoadmapState,
        stateKey,
        roadmapConfig,
        stateConfig,
        mergedConfig
      });
      
      return mergedConfig;
    } else if (selectedTile) {
      return tileConfigs[selectedTile] || {};
    } else if (selectedHeader) {
      return headerConfigs[selectedHeader] || {};
    }
    return {};
  };
  
  // Update config for the current context
  const updateConfig = (updates: Record<string, any>) => {
    if (selectedTile === 'roadmap') {
      // For roadmap tiles, update state-specific config
      const stateKeyMap = {
        'todo': 'todoState',
        'in_progress': 'inProgressState', 
        'complete': 'completeState'
      };
      const stateKey = stateKeyMap[selectedRoadmapState];
      const currentRoadmapConfig = tileConfigs.roadmap || {};
      const currentStateConfig = currentRoadmapConfig[stateKey] || {};
      
      // Create the nested update structure that the context expects
      const nestedUpdate = {
        [stateKey]: {
          ...currentStateConfig,
          ...updates
        }
      };
      
            updateTileConfig('roadmap', nestedUpdate);
    } else if (selectedTile) {
      updateTileConfig(selectedTile, updates);
    } else if (selectedHeader) {
      updateHeaderConfig(selectedHeader, updates);
    }
  };

  // Calculate initial position relative to modal
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const modalRect = modalRef.current.getBoundingClientRect();
      setPosition({
        x: modalRect.right + 10, // 10px gap from modal
        y: modalRect.top
      });
    }
  }, [isOpen, modalRef]);

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || (!selectedTile && !selectedHeader)) return null;

  const isHeader = selectedHeader !== null;
  const title = isHeader 
    ? `${selectedHeader === 'row' ? 'Row' : 'Column'} Header Defaults`
    : `${tileTypes.find(t => t.id === selectedTile)?.name} Defaults`;

  const config = getConfig();

  const panelContent = (
    <div
      ref={panelRef}
      className={`fixed bg-white border border-gray-300 rounded-lg shadow-2xl w-80 max-h-[80vh] z-[100] ${
        isDragging ? 'cursor-grabbing' : ''
      }`}
      style={{
        left: position.x,
        top: position.y,
        transform: isDragging ? 'scale(1.02)' : 'scale(1)',
        transition: isDragging ? 'none' : 'transform 0.2s ease'
      }}
    >
      {/* Draggable Header */}
      <div
        className={`flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200 rounded-t-lg ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center space-x-2">
          <GripHorizontal className="h-4 w-4 text-gray-400" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Appearance Defaults</h3>
            <p className="text-xs text-gray-600">{title}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Settings Content */}
      <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
        <div className="space-y-6">
          {/* Roadmap State Selector - Only show for roadmap tiles */}
          {selectedTile === 'roadmap' && (
            <div className="pb-4 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">Roadmap State</label>
              <select
                value={selectedRoadmapState}
                onChange={(e) => {
                  console.log('🎯 Switching roadmap state from', selectedRoadmapState, 'to', e.target.value);
                  console.log('🎯 Current config before switch:', getConfig());
                  setSelectedRoadmapState(e.target.value as 'todo' | 'in_progress' | 'complete');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50 border-blue-300 text-blue-900 font-medium"
              >
                <option value="todo">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="complete">Completed</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                <strong>Editing: {selectedRoadmapState.replace('_', ' ').toUpperCase()}</strong><br/>
                Configure appearance defaults for each roadmap state. Settings below will apply to the selected state only.
              </p>
            </div>
          )}

          {/* TEXT SETTINGS */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">TEXT</h4>
            
            {/* Title Text Controls */}
            <ColorPicker
              label="Title Text Color"
              value={config.textColor || '#111827'}
              onChange={(color) => updateConfig({ textColor: color })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title Text Size</label>
              <select 
                value={config.fontSize || '14px'}
                onChange={(e) => updateConfig({ fontSize: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="10px">Extra Small (10px)</option>
                <option value="12px">Small (12px)</option>
                <option value="14px">Medium (14px)</option>
                <option value="16px">Large (16px)</option>
                <option value="18px">Extra Large (18px)</option>
                <option value="20px">XX Large (20px)</option>
                <option value="24px">Huge (24px)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title Text Style</label>
              <div className="flex space-x-2">
                <button
                  className={`px-3 py-1 text-sm border rounded ${
                    config.fontWeight === 'bold' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'
                  }`}
                  onClick={() => {
                    const newWeight = config.fontWeight === 'bold' ? 'normal' : 'bold';
                    updateConfig({ fontWeight: newWeight });
                  }}
                >
                  <strong>B</strong>
                </button>
                <button
                  className={`px-3 py-1 text-sm border rounded ${
                    config.fontStyle === 'italic' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'
                  }`}
                  onClick={() => {
                    const newStyle = config.fontStyle === 'italic' ? 'normal' : 'italic';
                    updateConfig({ fontStyle: newStyle });
                  }}
                >
                  <em>I</em>
                </button>
                <button
                  className={`px-3 py-1 text-sm border rounded ${
                    config.textDecoration === 'underline' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'
                  }`}
                  onClick={() => {
                    const newDecoration = config.textDecoration === 'underline' ? 'none' : 'underline';
                    updateConfig({ textDecoration: newDecoration });
                  }}
                >
                  <u>U</u>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title Text Alignment</label>
              <select 
                value={config.textAlign || 'left'}
                onChange={(e) => {
                  if (selectedTile) {
                    // Update both textAlign and headerAlignment to ensure it works
                    updateConfig({ 
                      textAlign: e.target.value,
                      headerAlignment: e.target.value 
                    });
                  } else if (selectedHeader) {
                    updateConfig({ textAlign: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
                <option value="justify">Justify</option>
              </select>
            </div>

            {/* Vertical Alignment - Only for row headers */}
            {selectedHeader === 'row' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title Vertical Alignment</label>
                <select 
                  value={config.alignItems || 'center'}
                  onChange={(e) => updateConfig({ alignItems: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="top">Top</option>
                  <option value="center">Middle</option>
                  <option value="bottom">Bottom</option>
                </select>
              </div>
            )}

            {/* Description Text Controls - Only for tiles */}
            {selectedTile && (
              <>
                <ColorPicker
                  label="Description Text Color"
                  value={config.descriptionTextColor || '#6b7280'}
                  onChange={(color) => updateConfig({ descriptionTextColor: color })}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description Text Size</label>
                  <select 
                    value={config.descriptionFontSize || '12px'}
                    onChange={(e) => updateConfig({ descriptionFontSize: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10px">Extra Small (10px)</option>
                    <option value="12px">Small (12px)</option>
                    <option value="14px">Medium (14px)</option>
                    <option value="16px">Large (16px)</option>
                    <option value="18px">Extra Large (18px)</option>
                    <option value="20px">XX Large (20px)</option>
                    <option value="24px">Huge (24px)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description Text Style</label>
                  <div className="flex space-x-2">
                    <button
                      className={`px-3 py-1 text-sm border rounded ${
                        config.descriptionFontWeight === 'bold' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'
                      }`}
                      onClick={() => {
                        const newWeight = config.descriptionFontWeight === 'bold' ? 'normal' : 'bold';
                        updateConfig({ descriptionFontWeight: newWeight });
                      }}
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      className={`px-3 py-1 text-sm border rounded ${
                        config.descriptionFontStyle === 'italic' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'
                      }`}
                      onClick={() => {
                        const newStyle = config.descriptionFontStyle === 'italic' ? 'normal' : 'italic';
                        updateConfig({ descriptionFontStyle: newStyle });
                      }}
                    >
                      <em>I</em>
                    </button>
                    <button
                      className={`px-3 py-1 text-sm border rounded ${
                        config.descriptionTextDecoration === 'underline' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'
                      }`}
                      onClick={() => {
                        const newDecoration = config.descriptionTextDecoration === 'underline' ? 'none' : 'underline';
                        updateConfig({ descriptionTextDecoration: newDecoration });
                      }}
                    >
                      <u>U</u>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description Text Alignment</label>
                  <select 
                    value={config.descriptionTextAlign || 'left'}
                    onChange={(e) => updateConfig({ descriptionTextAlign: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                    <option value="justify">Justify</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {/* BACKGROUND SETTINGS */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">BACKGROUND</h4>
            
            <ColorPicker
              label="Background Color"
              value={config.backgroundColor || '#ffffff'}
              onChange={(color) => updateConfig({ backgroundColor: color })}
            />

            <ColorPicker
              label="Gradient Color"
              value={config.gradientColor || ''}
              onChange={(color) => updateConfig({ gradientColor: color })}
              showOpacity={true}
              allowClear={true}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gradient Direction</label>
              <select 
                value={config.gradientDirection || 'to-br'}
                onChange={(e) => updateConfig({ gradientDirection: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="to-r">Left to Right</option>
                <option value="to-l">Right to Left</option>
                <option value="to-b">Top to Bottom</option>
                <option value="to-t">Bottom to Top</option>
                <option value="to-br">Top-Left to Bottom-Right</option>
                <option value="to-bl">Top-Right to Bottom-Left</option>
                <option value="to-tr">Bottom-Left to Top-Right</option>
                <option value="to-tl">Bottom-Right to Top-Left</option>
              </select>
            </div>

            {/* Background Image - Only for tiles */}
            {selectedTile && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Image</label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Convert to base64 or handle upload
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const imageUrl = event.target?.result as string;
                            updateConfig({ backgroundImage: imageUrl });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {config.backgroundImage && (
                      <button
                        onClick={() => updateConfig({ backgroundImage: null })}
                        className="w-full px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm"
                      >
                        Remove Background Image
                      </button>
                    )}
                  </div>
                </div>

                {config.backgroundImage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image Fit</label>
                    <select 
                      value={config.backgroundImageFit || 'fill'}
                      onChange={(e) => updateConfig({ backgroundImageFit: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="fill">Fill</option>
                      <option value="fit">Fit</option>
                      <option value="stretch">Stretch</option>
                    </select>
                  </div>
                )}
              </>
            )}
          </div>

          {/* BORDER SETTINGS - Only for tiles, OUTLINE for headers */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">BORDER</h4>
            
            <ColorPicker
              label={`${selectedHeader ? 'Outline' : 'Border'} Color`}
              value={config.borderColor || config.outlineColor || '#e5e7eb'}
              onChange={(color) => {
                const property = selectedHeader ? 'outlineColor' : 'borderColor';
                updateConfig({ [property]: color });
              }}
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {selectedHeader ? 'Outline' : 'Border'} Thickness
              </label>
              <select 
                value={config.borderThickness || config.outlineThickness || '1'}
                onChange={(e) => {
                  const property = selectedHeader ? 'outlineThickness' : 'borderThickness';
                  updateConfig({ [property]: e.target.value });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">None</option>
                <option value="1">Thin (1px)</option>
                <option value="2">Medium (2px)</option>
                <option value="3">Thick (3px)</option>
                <option value="4">Extra Thick (4px)</option>
              </select>
            </div>

            {/* Apply border to background - Only for headers */}
            {selectedHeader && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="applyBorderToBackground"
                  checked={config.applyBorderToBackground || false}
                  onChange={(e) => updateConfig({ applyBorderToBackground: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="applyBorderToBackground" className="text-sm font-medium text-gray-700">
                  Apply border to background area
                </label>
              </div>
            )}
          </div>

          {/* Tile-specific settings */}
          {selectedTile && (
            <>
              {/* SHAPE SETTINGS */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">SHAPE</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tile Shape</label>
                  <select 
                    value={config.shape || 'rectangle'}
                    onChange={(e) => updateConfig({ shape: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="rectangle">Rectangle</option>
                    <option value="rounded-rectangle">Rounded Rectangle</option>
                    <option value="oval">Oval</option>
                    <option value="chevron">Chevron</option>
                    <option value="parallelogram">Parallelogram</option>
                    <option value="hexagon">Hexagon</option>
                    <option value="diamond">Diamond</option>
                  </select>
                </div>
                
                <ColorPicker
                  label="Divider Color"
                  value={config.dividerColor || '#e5e7eb'}
                  onChange={(color) => updateConfig({ dividerColor: color })}
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Divider Thickness</label>
                  <select 
                    value={config.dividerThickness || '1px'}
                    onChange={(e) => updateConfig({ dividerThickness: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0px">None</option>
                    <option value="1px">Thin (1px)</option>
                    <option value="2px">Medium (2px)</option>
                    <option value="3px">Thick (3px)</option>
                  </select>
                </div>
              </div>

              {/* WARNING LABELS - For tiles with warning indicators */}
              {(selectedTile === 'external_link' || selectedTile === 'shortcut') && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">WARNING LABELS</h4>
                  
                  <ColorPicker
                    label="Warning Text Color"
                    value={config.warningTextColor || '#d97706'}
                    onChange={(color) => updateConfig({ warningTextColor: color })}
                  />

                  <ColorPicker
                    label="Warning Background Color"
                    value={config.warningBackgroundColor || '#fef3c7'}
                    onChange={(color) => updateConfig({ warningBackgroundColor: color })}
                  />
                </div>
              )}



              {/* BEHAVIOR SETTINGS */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">BEHAVIOR</h4>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="restrict-delete"
                    checked={config.restrictDelete || false}
                    onChange={(e) => updateConfig({ restrictDelete: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="restrict-delete" className="text-sm font-medium text-gray-700">
                    Restrict Delete
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  When enabled, users will need special permissions to delete tiles of this type.
                </p>
              </div>
            </>
          )}

          {/* Apply to All Button - After dividing line, no section name */}
          <div className="pt-4 border-t border-gray-200">
            <button
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium text-sm"
              title={`Apply this visual style to all existing ${selectedTile ? tileTypes.find(t => t.id === selectedTile)?.name.toLowerCase() : 'header'} ${selectedTile ? 'tiles' : 'elements'} throughout your organisation`}
              onClick={() => {
                // TODO: Implement apply to all functionality
                showAlert('Apply to all functionality coming soon!');
              }}
            >
              Apply to all
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(panelContent, document.body);
}
